'use client';

/**
 * Platform settings: live values, where each came from, and audited writes.
 *
 * The point of this tab is PROVENANCE, not just editing. `platform_settings` was
 * empty on production for the entire launch build, so every flag ran on its coded
 * default and no surface said so. `purchases.markets` defaulted to `null`, which
 * checkout reads as "open in every market", so purchases were live worldwide
 * before merchant-of-record cover existed — because nobody had written a row.
 * (The default is now `['US']`; the visibility problem it exposed is why this
 * tab exists.)
 *
 * So every setting renders with a source badge. "Default" is shown as a real
 * state, not an absence, because the whole failure was that absence looked like
 * a decision.
 *
 * Access is enforced by the API route (staff read, admin write); this renders the
 * 403 as a friendly message and hides the controls when `canWrite` is false.
 *
 * The e2e pass of 2026-10-06 (scenarios REG-05, REG-27 and R-20) found three
 * ways this tab misread a value, all fixed here:
 *  - REG-27: `render()` joined every array with ", ", so a list of OBJECTS
 *    (ads.house, the house ads) read "[object Object]" in the row, in its
 *    history and in the danger confirm. Only a list of plain strings or
 *    numbers is joined now; anything else is shown as JSON.
 *  - R-20: a JSON setting's edit box was prefilled with that display text
 *    ("boardy", or "[] (empty)"), which is not JSON, so editing a list in
 *    place (adding an address to relay.agentCorrespondents; at the time, an
 *    agent id to relay.autoReplyAgents, a key retired on 2026-10-08) always
 *    failed "not valid JSON" unless the operator retyped the whole value. It
 *    is prefilled with the value's JSON now.
 *  - REG-05: a boolean danger setting (models.registry.enabled) had no "Why"
 *    field, so the flip was recorded with no reason, and its history read
 *    "null -> true". A boolean row takes a reason too, and a null in the
 *    history (no stored row: the coded default) reads "Default".
 */

import { useCallback, useEffect, useState } from 'react';
import { Database, History, RefreshCw, Save, Settings2, ShieldAlert, TriangleAlert } from 'lucide-react';

type SettingSource = 'env-force' | 'db' | 'env' | 'default';

interface SettingRow {
  key: string;
  value: unknown;
  source: SettingSource;
  defaultValue: unknown;
  envOverridden: boolean;
  label: string;
  help: string;
  kind: 'boolean' | 'number' | 'countries' | 'json';
  danger?: boolean;
  ownedElsewhere?: string;
}

interface AuditRow {
  id: string;
  key: string;
  oldValue: unknown;
  newValue: unknown;
  note: string | null;
  createdAt: string;
  by: string;
}

interface Readout {
  canWrite: boolean;
  settings: SettingRow[];
  audit: AuditRow[];
}

const SOURCE_LABEL: Record<SettingSource, string> = {
  'env-force': 'Env (forced)',
  db: 'Set here',
  env: 'Env var',
  default: 'Default',
};

/**
 * `default` is amber rather than neutral on purpose: an unset money setting is
 * the condition that shipped purchases open worldwide, and it should not read as
 * a calm steady state.
 */
const SOURCE_STYLE: Record<SettingSource, string> = {
  'env-force': 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  db: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  env: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  default: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
};

export function render(value: unknown): string {
  if (value === null) return 'null';
  if (value === undefined) return 'unset';
  if (Array.isArray(value)) {
    if (value.length === 0) return '[] (empty)';
    // A list of objects joined with ", " is "[object Object], ..." (REG-27).
    return value.every((v) => typeof v === 'string' || typeof v === 'number') ? value.join(', ') : JSON.stringify(value);
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * What a JSON setting's edit box starts with: the value's JSON, so an edit in
 * place parses (R-20). Null or unset starts empty, which Save stores as null
 * (clear the row, back to the default). Countries and numbers keep their own
 * plain prefill.
 */
export function editPrefill(row: Pick<SettingRow, 'kind' | 'value'>): string {
  if (row.kind === 'countries' && Array.isArray(row.value)) return row.value.join(', ');
  if (row.kind === 'json') return row.value === null || row.value === undefined ? '' : JSON.stringify(row.value);
  return render(row.value);
}

/**
 * A history value. A null there means no row existed (the old value of a
 * first write) or the row was cleared (`setSetting(null)` deletes it), which
 * is the coded default either way, so it reads as the Default badge does
 * rather than "null" (REG-05: "null -> true").
 */
function historyValue(value: unknown): string {
  return value === null || value === undefined ? SOURCE_LABEL.default : render(value);
}

/**
 * The POST's answer for one key, laid over a readout. The write route answers
 * with the setting as `describeSettings` sees it AFTER the write (value and
 * provenance), which is more authoritative for that row than any GET: the
 * service worker used to answer the follow-up GET from a cache captured
 * before the write (fixed in the bypass list, 2026-09-27), and a row that
 * shows the stale value after a green save is exactly the bug the e2e run
 * of 2026-09-26 reported. The overlay keeps the row's UI metadata (label,
 * help, kind), which the write route does not repeat.
 */
type Overlay = { key: string; setting: Partial<SettingRow> };
function applyOverlay(readout: Readout, overlay: Overlay | undefined): Readout {
  if (!overlay) return readout;
  return {
    ...readout,
    settings: readout.settings.map((s) => (s.key === overlay.key ? { ...s, ...overlay.setting } : s)),
  };
}

export function SettingsTab() {
  const [data, setData] = useState<Readout | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  // A save failure belongs on the row that failed, beside its Save button.
  // One banner above every row (the old `setError`) was off screen for any
  // row below the fold, so a 500 on a save looked like nothing happened.
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  const load = useCallback(async (overlay?: Overlay) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/settings', { credentials: 'include', cache: 'no-store' });
      if (res.status === 403) { setForbidden(true); return; }
      if (!res.ok) throw new Error(`Failed to load (${res.status})`);
      setData(applyOverlay((await res.json()) as Readout, overlay));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const setRowError = useCallback((key: string, message: string | null) => {
    setRowErrors((errs) => {
      const next = { ...errs };
      if (message) next[key] = message;
      else delete next[key];
      return next;
    });
  }, []);

  const save = useCallback(async (row: SettingRow, value: unknown) => {
    // A danger setting is felt by every user the moment it lands, so make the
    // operator say yes to the specific change rather than to a generic prompt.
    if (row.danger) {
      const ok = window.confirm(
        `${row.label}\n\nChange from:  ${render(row.value)}\nto:           ${render(value)}\n\nThis takes effect for everyone within about a minute.`,
      );
      if (!ok) return;
    }
    setSaving(row.key);
    setRowError(row.key, null);
    try {
      const res = await fetch('/api/admin/settings', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: row.key, value, note: notes[row.key] || undefined }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Save failed (${res.status})`);
      setDrafts((d) => { const next = { ...d }; delete next[row.key]; return next; });
      setNotes((n) => { const next = { ...n }; delete next[row.key]; return next; });
      const overlay: Overlay | undefined =
        body?.setting && typeof body.setting === 'object'
          ? { key: row.key, setting: body.setting as Partial<SettingRow> }
          : undefined;
      // The row shows the write's answer at once; the readout (and the audit
      // trail under it) refreshes behind it, with the same answer laid over.
      if (overlay) setData((d) => (d ? applyOverlay(d, overlay) : d));
      await load(overlay);
    } catch (e) {
      setRowError(row.key, e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(null);
    }
  }, [load, notes, setRowError]);

  if (forbidden) {
    return (
      <div className="flex items-start gap-3 p-4 rounded-lg bg-muted text-sm">
        <ShieldAlert className="h-5 w-5 shrink-0 text-muted-foreground" />
        <div>Platform settings are visible to staff only.</div>
      </div>
    );
  }

  if (loading && !data) {
    return <div className="p-6 text-sm text-muted-foreground">Loading settings…</div>;
  }

  const canWrite = !!data?.canWrite;
  const unset = data?.settings.filter((s) => s.source === 'default').length ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Settings2 className="h-5 w-5" /> Platform settings
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Runtime flips, no deploy. Every change is recorded below with who made it and why.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {unset > 0 && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 text-sm">
          <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
          <div>
            <strong>{unset}</strong> setting{unset === 1 ? ' is' : 's are'} running on a coded
            default with no stored value. That is not visible anywhere else: the app behaves the
            same whether a default was chosen deliberately or never touched.
          </div>
        </div>
      )}

      {!canWrite && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-muted text-sm">
          <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
          <div>Read-only. These gate money and legal posture, so only admins can change them.</div>
        </div>
      )}

      {error && (
        <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>
      )}

      <div className="space-y-3">
        {data?.settings.map((row) => {
          const draft = drafts[row.key];
          const editable = canWrite && !row.ownedElsewhere;
          const busy = saving === row.key;
          const rowError = rowErrors[row.key];

          return (
            <div key={row.key} className="card p-5" data-testid={`setting-row-${row.key}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{row.label}</span>
                    <span className={`text-[11px] px-1.5 py-0.5 rounded ${SOURCE_STYLE[row.source]}`}>
                      {SOURCE_LABEL[row.source]}
                    </span>
                    {row.danger && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">
                        affects everyone
                      </span>
                    )}
                    {row.ownedElsewhere && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        edit in {row.ownedElsewhere}
                      </span>
                    )}
                  </div>
                  <code className="text-[11px] text-muted-foreground">{row.key}</code>
                  <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">{row.help}</p>
                </div>

                {/* `ml-auto` matters: the header is flex-wrap, so a long help
                    string pushes this column onto its own line, where a plain
                    `text-right` sits in a content-width box and reads as LEFT
                    aligned. Compare a short-help row with a long-help one. */}
                <div className="text-right shrink-0 ml-auto max-w-full min-w-0">
                  {/* Long JSON (Ad sources) ran off the card: cap and wrap (REG-27). */}
                  <div className="text-sm font-mono [overflow-wrap:anywhere]" data-testid={`setting-value-${row.key}`}>{render(row.value)}</div>
                  {row.source === 'default' && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">no stored value</div>
                  )}
                  {row.envOverridden && (
                    <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">
                      an env var is supplying this
                    </div>
                  )}
                </div>
              </div>

              {editable && (
                <div className="mt-3 pt-3 border-t flex items-end gap-2 flex-wrap">
                  {row.kind === 'boolean' ? (
                    <>
                      {/* The reason is recorded with a flip too (REG-05): a danger
                          switch is exactly the change an incident review asks about. */}
                      <div className="flex-1 min-w-[180px]">
                        <label className="block text-sm font-medium mb-2">Why (recorded)</label>
                        <input
                          value={notes[row.key] ?? ''}
                          onChange={(e) => setNotes((n) => ({ ...n, [row.key]: e.target.value }))}
                          placeholder="e.g. MoR green light for US"
                          className="w-full rounded-lg border bg-background px-3 py-1.5 text-sm"
                          data-testid={`setting-note-${row.key}`}
                        />
                      </div>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void save(row, !row.value)}
                        className="px-3 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors disabled:opacity-50"
                      >
                        {busy ? 'Saving…' : `Turn ${row.value ? 'off' : 'on'}`}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 min-w-[220px]">
                        <label className="block text-sm font-medium mb-2">
                          {row.kind === 'countries'
                            ? 'Country codes, comma separated. Empty = closed everywhere.'
                            : 'New value'}
                        </label>
                        <input
                          value={draft ?? editPrefill(row)}
                          onChange={(e) => setDrafts((d) => ({ ...d, [row.key]: e.target.value }))}
                          className="w-full rounded-lg border bg-background px-3 py-1.5 text-sm font-mono"
                        />
                      </div>
                      <div className="flex-1 min-w-[180px]">
                        <label className="block text-sm font-medium mb-2">Why (recorded)</label>
                        <input
                          value={notes[row.key] ?? ''}
                          onChange={(e) => setNotes((n) => ({ ...n, [row.key]: e.target.value }))}
                          placeholder="e.g. MoR green light for US"
                          className="w-full rounded-lg border bg-background px-3 py-1.5 text-sm"
                          data-testid={`setting-note-${row.key}`}
                        />
                      </div>
                      <button
                        type="button"
                        disabled={busy || draft === undefined}
                        onClick={() => {
                          const raw = (draft ?? '').trim();
                          let parsed: unknown;
                          if (row.kind === 'countries') {
                            parsed = raw === '' ? [] : raw.split(',').map((s) => s.trim()).filter(Boolean);
                          } else if (row.kind === 'number') {
                            parsed = Number(raw);
                          } else {
                            try { parsed = raw === '' ? null : JSON.parse(raw); }
                            catch { setRowError(row.key, `${row.label}: not valid JSON`); return; }
                          }
                          void save(row, parsed);
                        }}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                      >
                        <Save className="h-4 w-4" /> {busy ? 'Saving…' : 'Save'}
                      </button>
                    </>
                  )}
                  {rowError && (
                    <p
                      role="alert"
                      className="basis-full text-sm text-destructive"
                      data-testid={`setting-error-${row.key}`}
                    >
                      {rowError}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div>
        <h3 className="text-sm font-semibold flex items-center gap-2 mb-2">
          <History className="h-4 w-4" /> Change history
        </h3>
        {data?.audit.length ? (
          <div className="card divide-y divide-border">
            {data.audit.map((a) => (
              <div key={a.id} className="p-3 text-sm flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <code className="text-[11px] text-muted-foreground">{a.key}</code>
                  <div className="font-mono text-xs mt-0.5">
                    {historyValue(a.oldValue)} <span className="text-muted-foreground">→</span> {historyValue(a.newValue)}
                  </div>
                  {a.note && <div className="text-muted-foreground mt-1">{a.note}</div>}
                </div>
                <div className="text-xs text-muted-foreground text-right shrink-0">
                  <div>@{a.by}</div>
                  <div>{new Date(a.createdAt).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted text-sm text-muted-foreground">
            <Database className="h-4 w-4 shrink-0 mt-0.5" />
            <div>No settings have ever been changed. Every value above is a coded default.</div>
          </div>
        )}
      </div>
    </div>
  );
}
